import type { Entity, Issue, Label, Mutation, PlanComment, PlanDetail, PlanRow, Proposal, Question, RecordEntry, Run, Runner, RunnerJob, Snapshot, TranscriptItem, WireAttachment } from '../api/types'
import { uuid4 } from '../api/uuid'
import type { IssuePatch } from './store'

/** Only these two ids are kept outside the identity's own database. */
export interface LocalIdentity { workspaceId: string; memberId: string }
export interface OutboxEntry {
  mutationId: string
  mutation: Mutation
  issueId: string
  issueKey: string
  patch: IssuePatch
  created?: Issue
  /** The append-only entry shown until the server confirms it. */
  record?: RecordEntry
  answered?: Question
  /** A change to a plan: the plan it changes, shown at once with these fields. */
  planId?: string
  planPatch?: Partial<PlanRow>
  /** The plan `create_plan` makes, as the store expects it, shown until the server answers. */
  createdPlan?: PlanRow
  /** The body `set_plan_body` sends, shown on the plan's page until the server answers. */
  planBody?: string
  /** A session a person renames, shown with this title until the server answers. */
  sessionId?: string
  sessionTitle?: string
  /** A label edited or deleted, shown until the server confirms it. */
  label?: Label
  /** A comment on a plan added or edited, shown until the server answers. */
  comment?: PlanComment
}
export interface RefusedChange extends OutboxEntry {
  message: string
  reason?: string
  detail?: unknown
}
export interface LocalBase {
  snapshot: Snapshot
  snapshotSeq: number
  rowSeq: [string, number][]
}
export interface LocalState {
  base: LocalBase | null
  outbox: OutboxEntry[]
  refused: RefusedChange[]
}

/** Kept before reading files or dispatching. No attachments means dispatch has not begun. */
export interface PendingPrompt {
  session: string
  mutationId: string
  text: string
  files: { name: string; size: number; type: string; lastModified: number }[]
  attachments?: WireAttachment[]
  /** Where it goes while a turn runs: into the turn (`now`, Return) or behind it (`later`, ⌘Return); a retry keeps it (Poulpe-703). */
  priority?: 'now' | 'later'
  /** A definite refusal permits editing the restored prompt before another attempt. */
  refused?: boolean
}

function request<T>(req: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    req.onsuccess = () => resolve(req.result)
    req.onerror = () => reject(req.error)
  })
}
function completed(tx: IDBTransaction): Promise<void> {
  return new Promise((resolve, reject) => {
    tx.oncomplete = () => resolve()
    tx.onabort = () => reject(tx.error ?? new Error('Local storage transaction aborted.'))
    tx.onerror = () => reject(tx.error)
  })
}
function open(name: string, stores: string[], version = 1): Promise<IDBDatabase> {
  const req = indexedDB.open(name, version)
  req.onupgradeneeded = () => {
    for (const store of stores) if (!req.result.objectStoreNames.contains(store)) req.result.createObjectStore(store)
  }
  return request(req).then((db) => {
    db.onversionchange = () => db.close()
    return db
  })
}
const kinds = {
  projects: 'project', labels: 'label', issues: 'issue', record_entries: 'record_entry',
  questions: 'question', sessions: 'session', proposals: 'proposal', returned: 'proposal', landings: 'landing', plans: 'plan', runs: 'run', attachments: 'attachment',
  runners: 'runner', jobs: 'job',
} as const

export const TRANSCRIPT_CAP = 50 * 1024 * 1024
export interface KeptTranscript {
  session: string
  items: TranscriptItem[]
  after: string | null
  bytes: number
  lastRead: number
}
export type TranscriptCache = Pick<LocalStore, 'readTranscript' | 'appendTranscript'> & {
  beforeClose?: (listener: () => void) => () => void
}

/** Plans' details kept for a cold start offline: the least recently read go first beyond this. */
export const PLAN_CAP = 20 * 1024 * 1024
export const PLAN_COUNT = 200
interface KeptPlan { detail: PlanDetail; bytes: number; lastRead: number }

/** Attachment bytes read before, for opening offline: one file's most, and all together; the least recently read go first. */
export const FILE_MAX = 20 * 1024 * 1024
export const FILE_CAP = 100 * 1024 * 1024
interface KeptFileMeta { size: number; lastRead: number }
interface KeptFile { bytes: ArrayBuffer; type: string }

function bytesOf(blob: Blob): Promise<ArrayBuffer> {
  if (typeof blob.arrayBuffer === 'function') return blob.arrayBuffer()
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(reader.result as ArrayBuffer)
    reader.onerror = () => reject(reader.error)
    reader.readAsArrayBuffer(blob)
  })
}

// UTF-16 JSON length estimates the space used without encoding every item.
const itemBytes = (item: TranscriptItem): number => JSON.stringify(item).length * 2
const transcriptRange = (session: string): IDBKeyRange => IDBKeyRange.bound([session], [session, []])

/** A transaction contains the confirmed rows and the pending effect together. */
export class LocalStore {
  private closed = false
  /** This store's own mark in `meta`, so that it knows when another tab wrote after it. */
  private readonly token = uuid4()
  /** The rows this store last wrote, by key, while no other tab has written since. */
  private written: Map<string, { entity: string; data: { id: string } }> | null = null
  private readonly closing = new Set<() => void>()

  constructor(private readonly db: IDBDatabase) {
    db.onversionchange = () => this.close()
  }

  beforeClose(listener: () => void): () => void {
    if (this.closed) { listener(); return () => {} }
    this.closing.add(listener)
    return () => { this.closing.delete(listener) }
  }

  close(): void {
    if (this.closed) return
    // Followers open their final write transaction before close disallows it.
    for (const listener of [...this.closing]) listener()
    this.closing.clear()
    this.closed = true
    this.db.close()
  }

  async readPrompt(session: string): Promise<PendingPrompt | null> {
    const tx = this.db.transaction('promptSends')
    const done = completed(tx)
    const held = await request(tx.objectStore('promptSends').get(session)) as PendingPrompt | undefined
    await done
    return held ?? null
  }

  async keepPrompt(prompt: PendingPrompt): Promise<void> {
    const tx = this.db.transaction('promptSends', 'readwrite')
    const done = completed(tx)
    const store = tx.objectStore('promptSends')
    const read = store.get(prompt.session)
    let conflict: Error | null = null
    read.onsuccess = () => {
      const before = read.result as PendingPrompt | undefined
      // Another open composer must not replace an unresolved send. Once its
      // bytes were dispatchable, the same id must keep the same arguments too.
      if (before && !before.refused && (before.mutationId !== prompt.mutationId ||
        (before.attachments && (before.text !== prompt.text || JSON.stringify(before.attachments) !== JSON.stringify(prompt.attachments))))) {
        conflict = new Error('An earlier prompt is still being sent. Reload before sending another.')
        tx.abort()
        return
      }
      store.put(prompt, prompt.session)
    }
    try { await done }
    catch (err) { throw conflict ?? err }
  }

  /** A late answer must not delete a newer send for this session. */
  async finishPrompt(session: string, mutationId: string): Promise<void> {
    const tx = this.db.transaction('promptSends', 'readwrite')
    const done = completed(tx)
    const store = tx.objectStore('promptSends')
    const held = store.get(session)
    held.onsuccess = () => {
      if ((held.result as PendingPrompt | undefined)?.mutationId === mutationId) store.delete(session)
    }
    await done
  }

  async readTranscript(session: string): Promise<KeptTranscript | null> {
    const tx = this.db.transaction(['transcripts', 'transcriptMeta'], 'readwrite')
    const done = completed(tx)
    const metadata = tx.objectStore('transcriptMeta')
    const req = metadata.get(session)
    let kept: KeptTranscript | null = null
    req.onsuccess = () => {
      const meta = req.result as Omit<KeptTranscript, 'items'> | undefined
      if (!meta) return
      const read = tx.objectStore('transcripts').getAll(transcriptRange(session))
      read.onsuccess = () => {
        meta.lastRead = Date.now()
        kept = { ...meta, items: read.result as TranscriptItem[] }
        metadata.put(meta, session)
      }
    }
    await done
    return kept
  }

  async appendTranscript(session: string, fresh: readonly TranscriptItem[], after: string | null, cap = TRANSCRIPT_CAP): Promise<void> {
    const tx = this.db.transaction(['transcripts', 'transcriptMeta'], 'readwrite')
    const done = completed(tx)
    const store = tx.objectStore('transcripts')
    const metadata = tx.objectStore('transcriptMeta')
    const req = metadata.getAll()
    req.onsuccess = () => {
      const all = req.result as Omit<KeptTranscript, 'items'>[]
      const before = all.find(row => row.session === session)
      const meta: Omit<KeptTranscript, 'items'> = {
        session, bytes: before?.bytes ?? 0, lastRead: Date.now(),
        after: before?.after && (!after || before.after > after) ? before.after : after,
      }
      const commit = () => {
        const others = all.filter(row => row.session !== session).sort((a, b) => a.lastRead - b.lastRead)
        let total = meta.bytes + others.reduce((sum, row) => sum + row.bytes, 0)
        for (const row of others) {
          if (total <= cap) break
          store.delete(transcriptRange(row.session))
          metadata.delete(row.session)
          total -= row.bytes
        }
        metadata.put(meta, session)
      }
      const finish = () => {
        if (meta.bytes <= cap) { commit(); return }
        // IDs are the server's sequence. Only eviction reads old payloads.
        const trim = store.openCursor(transcriptRange(session))
        trim.onsuccess = () => {
          const row = trim.result
          if (!row) { commit(); return }
          meta.bytes -= itemBytes(row.value as TranscriptItem)
          row.delete()
          if (meta.bytes > cap) row.continue()
          else commit()
        }
      }
      const unique = new Map(fresh.map(item => [item.id, item]))
      let remaining = unique.size
      if (!remaining) { finish(); return }
      for (const item of unique.values()) {
        const key = [session, item.id]
        const known = store.getKey(key)
        known.onsuccess = () => {
          if (known.result === undefined) {
            store.put(item, key)
            meta.bytes += itemBytes(item)
          }
          if (--remaining === 0) finish()
        }
      }
    }
    await done
  }

  /** Keep a plan's detail as last read, and evict the least recently read beyond the caps. */
  async keepPlan(detail: PlanDetail, cap = PLAN_CAP, count = PLAN_COUNT): Promise<void> {
    const tx = this.db.transaction('planDetails', 'readwrite')
    const done = completed(tx)
    const store = tx.objectStore('planDetails')
    const kept: KeptPlan = { detail, bytes: JSON.stringify(detail).length * 2, lastRead: Date.now() }
    store.put(kept, detail.id)
    const all = store.getAll()
    all.onsuccess = () => {
      const rows = (all.result as KeptPlan[]).filter(row => row.detail.id !== detail.id).sort((a, b) => a.lastRead - b.lastRead)
      let total = kept.bytes + rows.reduce((sum, row) => sum + row.bytes, 0)
      let n = rows.length + 1
      for (const row of rows) {
        if (total <= cap && n <= count) break
        store.delete(row.detail.id)
        total -= row.bytes
        n--
      }
    }
    await done
  }

  async readPlans(): Promise<PlanDetail[]> {
    const tx = this.db.transaction('planDetails')
    const done = completed(tx)
    const rows = await request(tx.objectStore('planDetails').getAll()) as KeptPlan[]
    await done
    return rows.map(row => row.detail)
  }

  /**
   * Keep a file the server just gave, for opening offline, and evict the
   * least recently read beyond the cap. A file that is empty or larger than
   * one file may be is not kept.
   */
  async keepFile(id: string, blob: Blob, cap = FILE_CAP, most = FILE_MAX): Promise<void> {
    if (blob.size === 0 || blob.size > most) return
    const kept: KeptFile = { bytes: await bytesOf(blob), type: blob.type }
    const tx = this.db.transaction(['attachmentFiles', 'attachmentFileMeta'], 'readwrite')
    const done = completed(tx)
    const files = tx.objectStore('attachmentFiles')
    const metadata = tx.objectStore('attachmentFileMeta')
    const meta: KeptFileMeta = { size: blob.size, lastRead: Date.now() }
    files.put(kept, id)
    metadata.put(meta, id)
    const keys = metadata.getAllKeys()
    const rows = metadata.getAll()
    rows.onsuccess = () => {
      const ids = keys.result as string[]
      const others = (rows.result as KeptFileMeta[]).map((row, i) => ({ id: ids[i]!, row })).filter(other => other.id !== id)
        .sort((a, b) => a.row.lastRead - b.row.lastRead)
      let total = meta.size + others.reduce((sum, other) => sum + other.row.size, 0)
      for (const other of others) {
        if (total <= cap) break
        files.delete(other.id)
        metadata.delete(other.id)
        total -= other.row.size
      }
    }
    await done
  }

  /** A file kept before, or null. Reading it marks it recently read. */
  async readFile(id: string): Promise<Blob | null> {
    const tx = this.db.transaction(['attachmentFiles', 'attachmentFileMeta'], 'readwrite')
    const done = completed(tx)
    const metadata = tx.objectStore('attachmentFileMeta')
    const req = tx.objectStore('attachmentFiles').get(id)
    let blob: Blob | null = null
    req.onsuccess = () => {
      const kept = req.result as KeptFile | undefined
      if (!kept) return
      blob = new Blob([kept.bytes], { type: kept.type })
      const meta = metadata.get(id)
      meta.onsuccess = () => { if (meta.result) metadata.put({ ...(meta.result as KeptFileMeta), lastRead: Date.now() }, id) }
    }
    await done
    return blob
  }

  async dropFile(id: string): Promise<void> {
    const tx = this.db.transaction(['attachmentFiles', 'attachmentFileMeta'], 'readwrite')
    const done = completed(tx)
    tx.objectStore('attachmentFiles').delete(id)
    tx.objectStore('attachmentFileMeta').delete(id)
    await done
  }

  /** Forget every kept file: the person signed out. */
  async clearFiles(): Promise<void> {
    const tx = this.db.transaction(['attachmentFiles', 'attachmentFileMeta'], 'readwrite')
    const done = completed(tx)
    tx.objectStore('attachmentFiles').clear()
    tx.objectStore('attachmentFileMeta').clear()
    await done
  }

  async read(): Promise<LocalState> {
    const tx = this.db.transaction(['rows', 'meta', 'outbox', 'refused'])
    const done = completed(tx)
    const [rows, meta, outbox, refused] = await Promise.all([
      request(tx.objectStore('rows').getAll()) as Promise<Entity[]>,
      request(tx.objectStore('meta').get('base')) as Promise<(Omit<LocalBase, 'snapshot'> & { cursor: number; epoch?: string | null }) | undefined>,
      request(tx.objectStore('outbox').getAll()) as Promise<OutboxEntry[]>,
      request(tx.objectStore('refused').getAll()) as Promise<RefusedChange[]>,
    ])
    await done
    const snapshot: Snapshot & { returned: Proposal[]; plans: PlanRow[]; runs: Run[]; runners: Runner[]; jobs: RunnerJob[] } = { seq: meta?.cursor ?? 0, ...(meta?.epoch ? { epoch: meta.epoch } : {}), projects: [], labels: [], issues: [], record_entries: [], questions: [], sessions: [], proposals: [], returned: [], landings: [], plans: [], runs: [], attachments: [], runners: [], jobs: [] }
    for (const row of rows) {
      switch (row.entity) {
        case 'attachment': (snapshot.attachments ??= []).push(row.data); break
        case 'project': snapshot.projects.push(row.data); break
        case 'label': snapshot.labels.push(row.data); break
        case 'issue': snapshot.issues.push(row.data); break
        case 'record_entry': snapshot.record_entries.push(row.data); break
        case 'question': snapshot.questions.push(row.data); break
        case 'session': snapshot.sessions.push(row.data); break
        case 'proposal': (row.data.state === 'returned' ? snapshot.returned : snapshot.proposals).push(row.data); break
        case 'landing': snapshot.landings.push(row.data); break
        case 'plan': snapshot.plans.push(row.data); break
        case 'run': snapshot.runs.push(row.data); break
        case 'runner': snapshot.runners.push(row.data); break
        case 'job': snapshot.jobs.push(row.data); break
      }
    }
    return { base: meta ? { snapshot, snapshotSeq: meta.snapshotSeq, rowSeq: meta.rowSeq } : null, outbox, refused }
  }

  /**
   * Put the base: only the rows whose object changed since this store last
   * wrote, and delete those that left (Poulpe-710). A full rewrite of a big
   * board took seconds, and every change waited for it. The store replaces a
   * row's object whenever the row changes, so identity says what changed.
   * When another tab wrote last (`writer`), or this store has not written yet
   * or its last write failed, nothing is known of the disk: it writes every
   * row, as before.
   */
  private putBase(tx: IDBTransaction, base: LocalBase): void {
    const next = new Map<string, { entity: string; data: { id: string } }>()
    for (const [name, kind] of Object.entries(kinds)) {
      for (const data of base.snapshot[name as keyof typeof kinds] ?? []) next.set(`${kind}/${data.id}`, { entity: kind, data })
    }
    const rows = tx.objectStore('rows')
    const meta = tx.objectStore('meta')
    const writer = meta.get('writer')
    writer.onsuccess = () => {
      const last = writer.result === this.token ? this.written : null
      if (last) {
        for (const key of last.keys()) if (!next.has(key)) rows.delete(key)
        for (const [key, row] of next) if (last.get(key)?.data !== row.data) rows.put(row, key)
      } else {
        rows.clear()
        for (const [key, row] of next) rows.put(row, key)
      }
      meta.put(this.token, 'writer')
      this.written = next
    }
    meta.put({ cursor: base.snapshot.seq, epoch: base.snapshot.epoch ?? null, snapshotSeq: base.snapshotSeq, rowSeq: base.rowSeq }, 'base')
  }

  private async write(base: LocalBase, edit: (tx: IDBTransaction) => void): Promise<void> {
    const tx = this.db.transaction(['rows', 'meta', 'outbox', 'refused'], 'readwrite')
    const done = completed(tx)
    this.putBase(tx, base)
    edit(tx)
    try {
      await done
    } catch (err) {
      // What the disk holds is unknown now: the next write writes every row.
      this.written = null
      throw err
    }
  }

  replace(base: LocalBase): Promise<void> { return this.write(base, () => {}) }

  append(entry: OutboxEntry, base: LocalBase, refusedId?: string): Promise<void> {
    return this.write(base, (tx) => {
      const meta = tx.objectStore('meta')
      const counter = meta.get('next')
      counter.onsuccess = () => {
        const next = (counter.result as number | undefined) ?? 0
        tx.objectStore('outbox').add(entry, next)
        meta.put(next + 1, 'next')
      }
      if (refusedId) tx.objectStore('refused').delete(refusedId)
    })
  }

  /** Record the answer before removing a change, also when the feed confirms it. */
  finish(ids: readonly string[], base: LocalBase, refused?: RefusedChange): Promise<void> {
    return this.write(base, (tx) => {
      const cursor = tx.objectStore('outbox').openCursor()
      cursor.onsuccess = () => {
        const row = cursor.result
        if (!row) return
        if (ids.includes((row.value as OutboxEntry).mutationId)) row.delete()
        row.continue()
      }
      if (refused) tx.objectStore('refused').put(refused, refused.mutationId)
    })
  }

  async discard(id: string): Promise<void> {
    const tx = this.db.transaction('refused', 'readwrite')
    const done = completed(tx)
    tx.objectStore('refused').delete(id)
    await done
  }
}

export async function openLocal(origin: string, workspaceId: string, memberId: string): Promise<LocalStore> {
  return new LocalStore(await open(`poulpe.board.${JSON.stringify([origin, workspaceId, memberId])}`, ['rows', 'meta', 'outbox', 'refused', 'transcripts', 'transcriptMeta', 'planDetails', 'promptSends', 'attachmentFiles', 'attachmentFileMeta'], 5))
}

export async function lastIdentity(origin: string): Promise<LocalIdentity | null> {
  const db = await open('poulpe.identities', ['identities'])
  try {
    const tx = db.transaction('identities')
    const done = completed(tx)
    const identity = await request(tx.objectStore('identities').get(origin)) as LocalIdentity | undefined
    await done
    return identity ?? null
  } finally { db.close() }
}

export async function rememberIdentity(origin: string, identity: LocalIdentity | null): Promise<void> {
  const db = await open('poulpe.identities', ['identities'])
  try {
    const tx = db.transaction('identities', 'readwrite')
    const done = completed(tx)
    if (identity) tx.objectStore('identities').put(identity, origin)
    else tx.objectStore('identities').delete(origin)
    await done
  } finally { db.close() }
}
